(function () {
  'use strict';

  const groupId = (item) => item.properties?._customization_id;
  const variantId = (value) => String(value || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  const isBundle = (item) => item.has_components === true || item.item_components?.length > 0;

  function buildItems({ productVariantId, serviceVariantId, letters, font, collection }, id) {
    if (!/^[1-9]\d*$/.test(String(productVariantId || '')) ||
        !/^[1-9]\d*$/.test(String(serviceVariantId || '')) || !letters || !font || !id) {
      throw new Error('Incomplete personalization');
    }
    const properties = { Letters: letters, Font: font, _customization_id: id };
    return [
      { id: serviceVariantId, quantity: 1, properties: { ...properties, Product: String(productVariantId), Collection: collection || '' } },
      { id: productVariantId, quantity: 1, properties: { ...properties } }
    ];
  }

  // Never identify a partner by its position, variant alone, or engraving alone.
  function removalUpdates(cart, key, serviceProductId) {
    const items = cart.items;
    const selected = items.find((item) => item.key === key);
    if (!selected) throw new Error('Cart changed; refresh before removing the item');
    const updates = { [selected.key]: 0 };
    if (isBundle(selected)) return updates;
    const isService = (item) => serviceProductId && String(item.product_id) === String(serviceProductId);
    const matches = (product, service) => !isService(product) && isService(service) &&
      !isBundle(product) && !isBundle(service) &&
      variantId(service.properties?.Product) === String(product.variant_id || product.id) &&
      !!product.properties?.Letters && product.properties.Letters === service.properties?.Letters &&
      !!product.properties?.Font && product.properties.Font === service.properties?.Font &&
      product.quantity === service.quantity;

    if (groupId(selected)) {
      const group = items.filter((item) => groupId(item) === groupId(selected));
      if (group.length === 2) {
        const partner = group.find((item) => item.key !== key);
        if (matches(selected, partner) || matches(partner, selected)) updates[partner.key] = 0;
      }
      return updates;
    }

    // Old carts: remove the partner only when the match is unique in both directions.
    const legacyItems = items.filter((item) => !groupId(item) && !isBundle(item));
    const candidates = legacyItems.filter((item) => item.key !== key &&
      (matches(selected, item) || matches(item, selected)));
    if (candidates.length === 1) {
      const partner = candidates[0];
      const reverse = legacyItems.filter((item) => item.key !== partner.key &&
        (matches(partner, item) || matches(item, partner)));
      if (reverse.length === 1) updates[partner.key] = 0;
    }
    return updates;
  }

  function createId() {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  const cartUrl = (path) => `${window.Shopify?.routes?.root || '/'}cart${path}`;
  const errorText = () => window.cartStrings?.error || 'Unable to update your cart. Please try again.';

  function showError(container, message) {
    let error = container.querySelector('[data-personalization-cart-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.personalizationCartError = '';
      error.className = 'personalisation_block_error';
      error.setAttribute('role', 'alert');
      container.appendChild(error);
    }
    error.textContent = message;
    error.hidden = !message;
    error.style.display = message ? 'block' : 'none';
  }

  async function readResponse(response) {
    const body = await response.json();
    if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : errorText());
    return body;
  }

  async function add(dialog, engraving) {
    if (dialog.personalizationSubmitting) return;
    dialog.personalizationSubmitting = true;
    const button = dialog.formElem.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    showError(dialog.formElem, '');
    try {
      const section = dialog.closest('.shopify-section');
      const selectedVariant = section?.querySelector('#ajax-add-to-cart [name="id"]')?.value || dialog.dataset.variantId;
      const items = buildItems({
        productVariantId: selectedVariant,
        serviceVariantId: dialog.dataset.customProductId,
        ...engraving
      }, createId());
      await readResponse(await fetch(cartUrl('/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      }));
      dialog.close();
      if (window.ShpCustom?.Cart?.getCart) window.ShpCustom.Cart.getCart();
      else window.location.assign(cartUrl(''));
    } catch (error) {
      console.error('Personalization: add failed.', error);
      showError(dialog.formElem, errorText());
    } finally {
      dialog.personalizationSubmitting = false;
      if (button) button.disabled = false;
    }
  }

  let removing = false;
  async function remove(button) {
    if (removing) return;
    removing = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const container = button.parentElement;
    showError(container, '');
    try {
      const cart = await readResponse(await fetch(cartUrl('.js'), { cache: 'no-store' }));
      const updates = removalUpdates(cart, button.dataset.lineKey, window.dupontPersonalization?.serviceProductId);
      const keys = Object.keys(updates);
      const body = keys.length === 1 ? { id: keys[0], quantity: 0 } : { updates };
      const updatedCart = await readResponse(await fetch(cartUrl(keys.length === 1 ? '/change.js' : '/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }));
      window.dispatchCartUpdate?.(updatedCart, 'personalizationRemove');
      window.Nosto?.reloadCart();
      if (button.dataset.cartSurface === 'page') window.location.reload();
      else if (window.ShpCustom?.Cart?.getCart) window.ShpCustom.Cart.getCart();
      else window.location.reload();
    } catch (error) {
      console.error('Personalization: removal failed.', error);
      showError(container, errorText());
    } finally {
      removing = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  window.DupontPersonalizationCart = { buildItems, removalUpdates, add, remove };
}());
